import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { EDITOR_COLORS } from '../styles/editorTheme';

/**
 * Account tabs' checkbox: a 22px white box with a border and a dark check — replaces the blue
 * accent-filled boxes (and the native <input type="checkbox"> on web, which can't draw a dark
 * check on a white fill). Web draws the check as an inline SVG so its stroke is exact in every
 * browser; native keeps a heavy-check text glyph (no react-native-svg dependency in shared).
 */
export const OUTLINE_CHECKBOX_SIZE = 20;
const BORDER_COLOR = EDITOR_COLORS.textMuted;
const RADIUS = 4;
// Same color as the labels next to the checkboxes (textPrimary).
const MARK_COLOR = EDITOR_COLORS.textPrimary;
// SVG check stroke width in a 24-unit viewBox (web) — raise for a heavier check. The drawn
// stroke is STROKE * MARK_SIZE / 24 px (4.2 at 16px ≈ 2.8px), so it scales with the mark.
const STROKE = 4.2;
// Rendered size (px) of the SVG check inside the box (~80% of it).
const MARK_SIZE = 16;
// Tilt of the SVG check in degrees (negative = counter-clockwise).
const ROTATION = -7;
// Heavy check mark (U+2714) with a text-presentation selector so platforms don't swap in a
// colored emoji.
const NATIVE_MARK = '✔︎';

// Disabled look: pale border/fill and a gray check (kept visible so a locked-on setting still
// reads as checked).
const DISABLED_BORDER_COLOR = EDITOR_COLORS.controlBorder;
const DISABLED_BACKGROUND = '#f8fafc';
const DISABLED_MARK_COLOR = EDITOR_COLORS.placeholder;

/** Native box (also fine inside a Pressable on web). */
export const OutlineCheckboxBox: React.FC<{ checked: boolean; disabled?: boolean }> = ({ checked, disabled = false }) => (
  <View style={[styles.box, disabled && styles.boxDisabled]}>
    {checked ? <Text style={[styles.mark, disabled && styles.markDisabled]}>{NATIVE_MARK}</Text> : null}
  </View>
);

/** Web: a styled <button role="checkbox"> drawing the check as an SVG. */
export const renderWebOutlineCheckbox = (
  checked: boolean,
  onToggle: (nextChecked: boolean) => void,
  accessibilityLabel: string,
  disabled = false
) => {
  return React.createElement(
    'button' as any,
    {
      type: 'button',
      role: 'checkbox',
      'aria-checked': checked,
      'aria-label': accessibilityLabel,
      disabled,
      onClick: () => onToggle(!checked),
      style: {
        width: `${OUTLINE_CHECKBOX_SIZE}px`,
        height: `${OUTLINE_CHECKBOX_SIZE}px`,
        flexShrink: 0,
        padding: 0,
        boxSizing: 'border-box',
        border: `1px solid ${disabled ? DISABLED_BORDER_COLOR : BORDER_COLOR}`,
        borderRadius: `${RADIUS}px`,
        backgroundColor: disabled ? DISABLED_BACKGROUND : EDITOR_COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
      } as const,
    },
    checked
      ? React.createElement(
        'svg' as any,
        {
          width: MARK_SIZE,
          height: MARK_SIZE,
          viewBox: '0 0 24 24',
          'aria-hidden': true,
          focusable: 'false',
          style: { transform: `rotate(${ROTATION}deg)` },
        },
        React.createElement('path' as any, {
          d: 'M4.5 12.5l5 5L19.5 6.5',
          fill: 'none',
          stroke: disabled ? DISABLED_MARK_COLOR : MARK_COLOR,
          strokeWidth: STROKE,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        })
      )
      : null
  );
};

const styles = StyleSheet.create({
  box: {
    width: OUTLINE_CHECKBOX_SIZE,
    height: OUTLINE_CHECKBOX_SIZE,
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    borderRadius: RADIUS,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  boxDisabled: {
    borderColor: DISABLED_BORDER_COLOR,
    backgroundColor: DISABLED_BACKGROUND,
  },
  markDisabled: {
    color: DISABLED_MARK_COLOR,
  },
  mark: {
    fontFamily: 'Arial',
    fontSize: 14,
    color: MARK_COLOR,
    lineHeight: 14,
    fontWeight: '900',
  },
});
